import moment from "moment"
import type { GetServerSideProps, NextPage } from "next"
import Head from "next/head"
import { useRouter } from "next/router"
import { parseAsBoolean, useQueryState } from "nuqs"
import React, { type FC, type ReactElement, useContext, useEffect, useState } from "react"
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu"
import QRCode from "react-qr-code"

import { AppButton } from "../../../components/AppButton"
import AppLayout from "../../../components/AppLayout"
import AppNavigation from "../../../components/AppNavigation"
import { SaveOurSoundButton } from "../../../components/buttons/SaveOurSoundButton"
import CommunityContentContainer from "../../../components/CommunityContentContainer"
import { shouldHide } from "../../../components/ContentWrappingMenu"
import ScrollMenuArrow from "../../../components/icons/ScrollMenuArrow"
import { ModalBase } from "../../../components/ModalBase"
import { Skeleton } from "../../../components/Skeleton"
import UbiqButton, { UbiqButtonSize } from "../../../components/UbiqButton"
import {
  type BaseFeedItemFragment,
  type EventListItemFragment,
  UserActivityEventType,
  useCommunityContentQuery,
} from "../../../generated/graphql"
import { graphQLClient } from "../../../graphql/client"
import { UserContext } from "../../../providers/UserProvider"
import { CUSTOM_COMMUNITY_BUTTONS } from "../../../styling/types"
import { getCommunityIdBasedOnCode } from "../../../utils/community"
import { COMMUNITIES } from "../../../utils/customization"
import {
  DATE_FORMAT_DAY_MONTH_TIME,
  DATE_FORMAT_DAY_MONTH_YEAR,
  formatEvent,
} from "../../../utils/dates"
import { generateCommunityMetaTitle } from "../../../utils/metaTags"
import { fetchOgManifest } from "../../../utils/og-manifest"
import { getRequestBaseUrl, isClientSideNavigation, toAbsoluteUrl } from "../../../utils/requestUrl"
import { useTrackUserActivity } from "../../../utils/tracking"
import { useCommunityCustomization } from "../../../utils/useCommunityCustomization"
import { useEventImpressions } from "../../../utils/useEventImpressions"

interface WithLayout {
  getLayout: (page: ReactElement) => ReactElement
}

export type NextPageWithLayout<P = {}> = NextPage<P> & WithLayout

const NEWS_EVENTS_SCROLL_GESTURE_CLASS = "news-events-scroll-gesture"

const isEventPast = (eventEnd: Date | undefined | null) => moment().isBefore(eventEnd)

const RightArrow = () => {
  const { getNextElement, isLastItemVisible, scrollToItem } = useContext(VisibilityContext)
  const customizationData = useCommunityCustomization()

  const clickHandler = () => scrollToItem(getNextElement(), "smooth", "center")

  const arrowConfig = customizationData?.styles.global.scrollArrow

  return (
    <div
      className={`top-1/2 right-1 cursor-pointer transform -translate-y-1/2 absolute w-12 h-12 z-[4] opacity-100 sm:opacity-0 transition-all group-hover:opacity-100  ${
        isLastItemVisible ? "hidden" : ""
      }`}
      onClick={clickHandler}
    >
      <ScrollMenuArrow arrowColor={arrowConfig?.color} backgroundColor={arrowConfig?.background} />
    </div>
  )
}

const LeftArrow = () => {
  const { scrollToItem, getPrevElement, isFirstItemVisible } = React.useContext(VisibilityContext)
  const customizationData = useCommunityCustomization()

  const clickHandler = () => scrollToItem(getPrevElement(), "smooth", "center")

  const arrowConfig = customizationData?.styles.global.scrollArrow

  return (
    <div
      className={`top-1/2 -left-1 transform rotate-180 cursor-pointer -translate-y-1/2 absolute w-12 h-12 z-[4] opacity-100 sm:opacity-0 transition-all group-hover:opacity-100  ${
        isFirstItemVisible ? "hidden" : ""
      }`}
      onClick={clickHandler}
    >
      <ScrollMenuArrow arrowColor={arrowConfig?.color} backgroundColor={arrowConfig?.background} />
    </div>
  )
}

export const CommunityButton: FC<{
  title: string
  onClick?: () => void
  primaryButtonStyles: string
  button: CUSTOM_COMMUNITY_BUTTONS | string | undefined
}> = ({ title, button, primaryButtonStyles, onClick }) => {
  if (button === CUSTOM_COMMUNITY_BUTTONS.SAVE_OUR_SOUND) {
    return <SaveOurSoundButton onClick={onClick} title={title} />
  }

  return (
    <AppButton
      buttonStyles={primaryButtonStyles}
      onClick={onClick}
      text={title}
      type="button"
      wrapperStyles="!w-auto"
    />
  )
}

const Announcements: FC<{
  openModal: () => void
  announcements: BaseFeedItemFragment[] | undefined
}> = ({ announcements }) => {
  const customizationData = useCommunityCustomization()
  const track = useTrackUserActivity()
  const router = useRouter()

  if (!customizationData) return null
  const { styles: communityStyles, url } = customizationData

  const singlePageEnabled = customizationData.styles.announcement
  return (
    <div
      className={`flex flex-col gap-y-2 sm:mt-8 mt-5 sm:ml-6 pb-6 overflow-x-hidden ${
        communityStyles.communityLanding.announcements?.container ?? ""
      }`}
    >
      <h1 className={`pl-4 sm:pl-0 ${communityStyles.communityLanding.titles}`}>News</h1>
      {!announcements && (
        <div className="flex gap-2 sm:gap-6 pl-4 sm:pl-0 overflow-hidden">
          <Skeleton className="min-w-[327px] h-[260px] sm:min-w-[432px] sm:max-w-[432px] sm:h-[344px]" />
          <div className="hidden sm:flex flex-col gap-6">
            <Skeleton className="w-[205px] h-40" />
            <Skeleton className="w-[205px] h-40" />
          </div>
        </div>
      )}
      {announcements && announcements.length === 0 && (
        <p className={`sm:ml-0 ml-4 ${communityStyles.communityLanding.emptyOrLoadingState}`}>
          No news posted yet
        </p>
      )}
      {!!announcements?.length && (
        <ScrollMenu
          itemClassName="w-fit"
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
          scrollContainerClassName={`!h-[260px] content-start sm:!h-[344px] sm:flex-col sm:flex-wrap gap-2 sm:gap-6 hide-scroll pl-4 sm:pl-0 ${NEWS_EVENTS_SCROLL_GESTURE_CLASS}`}
          separatorClassName="hidden"
          wrapperClassName="relative group"
        >
          {announcements.map((a, key) => {
            const announcementsAssetStyles = `absolute left-0 top-0 h-full w-full object-cover`
            return (
              <div
                className={`relative flex justify-start ${
                  singlePageEnabled ? "cursor-pointer" : "cursor-default"
                } items-end 
                  ${communityStyles.communityLanding.announcements?.media ?? ""}
                  ${
                    key === 0
                      ? "min-w-[327px] sm:min-w-[432px] sm:max-w-[432px] h-full sm:min-h-[344px]"
                      : "min-w-[327px] sm:min-w-[205px] sm:max-w-[204px] h-full sm:h-40"
                  }`}
                key={key}
                onClick={() => {
                  track({
                    data: {
                      communityCode: customizationData?.code,
                      postId: a.id,
                    },
                    type: UserActivityEventType.OnlyMobilePopupOpened,
                  })
                  if (singlePageEnabled) {
                    void router.push(`/${url}/news/${a.id}`)
                  }
                }}
              >
                <div className="absolute z-[1] h-full w-full top-0 left-0 bg-black opacity-30" />
                {a.media?.[0] ? (
                  a.media[0].includes("type=video") ? (
                    <video
                      autoPlay
                      className={announcementsAssetStyles}
                      loop
                      muted
                      playsInline
                      src={a.media[0]}
                    />
                  ) : (
                    <img alt="" className={announcementsAssetStyles} src={a.media[0]} />
                  )
                ) : (
                  <div className="absolute left-0 top-0 h-full w-full bg-gray-400" />
                )}
                <div
                  className={`flex flex-col relative z-[2] pl-2 w-full ${
                    key === 0 ? "sm:pr-4 sm:pl-4 pb-1 sm:pb-3 " : "pr-2 pb-1"
                  }`}
                >
                  <p
                    className={`${
                      communityStyles.communityLanding.announcements?.customBg ?? "bg-[#232734]"
                    } text-white mb-1 px-1 py-1 w-fit ${
                      key === 0
                        ? (communityStyles.communityLanding.announcements?.lg?.subtitle ?? "")
                        : (communityStyles.communityLanding.announcements?.sm?.subtitle ?? "")
                    }`}
                  >
                    {moment(a.createdAt).format(DATE_FORMAT_DAY_MONTH_YEAR)}
                  </p>
                  <p
                    className={`text-white sm:truncate sm:text-clip sm:whitespace-normal sm two-line-truncate ${
                      key === 0
                        ? (communityStyles.communityLanding.announcements?.lg?.title ?? "")
                        : (communityStyles.communityLanding.announcements?.sm?.title ?? "")
                    }`}
                  >
                    {a.title}
                  </p>
                </div>
              </div>
            )
          })}
        </ScrollMenu>
      )}
      <div className="transform translate-y-[-70px] sm:transform-none mb-[-6px]" id="events" />
    </div>
  )
}
const AnnouncementsV2: FC<{
  openModal: () => void
  announcements: BaseFeedItemFragment[] | undefined
}> = ({ announcements, openModal }) => {
  const customizationData = useCommunityCustomization()
  const track = useTrackUserActivity()
  const router = useRouter()

  if (!customizationData) return null
  const { styles: communityStyles, url } = customizationData
  const announcementsV2Styles = communityStyles.communityLanding.announcementsV2

  if (!announcementsV2Styles) return null

  const singlePageEnabled = customizationData.styles.announcement
  return (
    <div
      className={`flex flex-col sm:mt-8 mt-5 sm:ml-6 pb-6 overflow-x-hidden ${
        announcementsV2Styles.sectionContainer ?? ""
      }`}
    >
      <h1
        className={`pl-4 sm:pl-0 ${communityStyles.communityLanding.titles} ${
          announcementsV2Styles.sectionTitle ?? ""
        }`}
      >
        News
      </h1>
      {!announcements && (
        <div
          className={`flex overflow-hidden ${announcementsV2Styles.scrollMenu?.container ?? ""}`}
        >
          {[0, 1, 2].map((key) => (
            <div className={announcementsV2Styles.item.container} key={key}>
              <div className={announcementsV2Styles.item.mediaContainer}>
                <div className={`animate-pulse ${announcementsV2Styles.item.placeholderMedia}`} />
              </div>
              <div className={announcementsV2Styles.item.content}>
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="mt-2 h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}
      {announcements && announcements.length === 0 && (
        <p className={`sm:ml-0 ml-4 ${communityStyles.communityLanding.emptyOrLoadingState}`}>
          No announcements posted yet
        </p>
      )}
      {!!announcements?.length && (
        <ScrollMenu
          itemClassName={announcementsV2Styles.scrollMenu?.item}
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
          scrollContainerClassName={`${announcementsV2Styles.scrollMenu?.container ?? ""} ${NEWS_EVENTS_SCROLL_GESTURE_CLASS}`}
          separatorClassName="hidden"
          wrapperClassName={announcementsV2Styles.scrollMenu?.wrapper}
        >
          {announcements.map((a, key) => (
            <div
              className={`cursor-pointer relative flex flex-col ${
                singlePageEnabled ? "cursor-pointer" : "cursor-default"
              } ${announcementsV2Styles.item.container}`}
              key={key}
              onClick={() => {
                track({
                  data: {
                    communityCode: customizationData?.code,
                    postId: a.id,
                  },
                  type: UserActivityEventType.OnlyMobilePopupOpened,
                })
                if (singlePageEnabled) {
                  void router.push(`/${url}/news/${a.id}`)
                } else {
                  openModal()
                }
              }}
            >
              <div className={announcementsV2Styles.item.mediaContainer}>
                {a.media?.[0] ? (
                  a.media[0].includes("type=video") ? (
                    <video
                      autoPlay
                      className={announcementsV2Styles.item.media}
                      loop
                      muted
                      playsInline
                      src={a.media[0]}
                    />
                  ) : (
                    <img alt="" className={announcementsV2Styles.item.media} src={a.media[0]} />
                  )
                ) : (
                  <div className={announcementsV2Styles.item.placeholderMedia} />
                )}
              </div>
              <div className={announcementsV2Styles.item.content}>
                <p className={`${announcementsV2Styles.item.title} line-clamp-2`}>{a.title}</p>
                <p className={announcementsV2Styles.item.subtitle}>
                  {moment(a.createdAt).format(DATE_FORMAT_DAY_MONTH_YEAR)}
                </p>
              </div>
            </div>
          ))}
        </ScrollMenu>
      )}
      <div className="transform translate-y-[-70px] sm:transform-none mb-[-6px]" id="events" />
    </div>
  )
}

const Events: FC<{ events: EventListItemFragment[] | undefined }> = ({ events }) => {
  const router = useRouter()
  const customizationData = useCommunityCustomization()
  const observe = useEventImpressions("community-landing")

  // Cards navigate via router.push, which doesn't prefetch; one prefetch warms the
  // shared /c/[code]/event/[id] bundle for every card. Skipped when only past
  // events are listed — those are rarely opened.
  const firstUpcomingEventId = events?.find((e) => moment().isBefore(e.end))?.id
  const communityUrl = customizationData?.url
  useEffect(() => {
    if (!firstUpcomingEventId || !communityUrl) return
    void router.prefetch(`${communityUrl}/event/${firstUpcomingEventId}`).catch(() => {})
  }, [communityUrl, firstUpcomingEventId, router])

  if (!customizationData) return null
  const { styles: communityStyles, url } = customizationData

  const handleEventClick = (event: EventListItemFragment) => {
    void router.push(`${url}/event/${event.id}`)
  }

  return (
    <div
      className={`flex flex-col sm:mx-6 mx-4 pb-8 ${
        communityStyles.communityLanding.event?.container ?? ""
      }`}
    >
      <span className="transform translate-y-[-130px] sm:translate-y-[-90px]" id="events" />
      <h1 className={`${communityStyles.communityLanding.titles} mb-2 mt-[-6px]`}>Events</h1>
      {!events && (
        <div className="flex gap-6 overflow-hidden">
          {[0, 1].map((key) => (
            <div
              className={`flex w-[410px] md:w-[456px] shrink-0 items-start ${key > 0 ? "hidden sm:flex" : ""}`}
              key={key}
            >
              <Skeleton className="h-[106px] sm:h-[110px] w-[192px] sm:w-[196px] mr-2 sm:mr-4 shrink-0" />
              <div className="flex-1 space-y-1 sm:space-y-2 mt-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}
      {events && events.length === 0 && (
        <p className={communityStyles.communityLanding.emptyOrLoadingState}>No events posted yet</p>
      )}
      {events && (
        <ScrollMenu
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
          scrollContainerClassName={`sm:pl-0 !h-full !max-h-[236px] sm:!max-h-[344px] flex-col flex-wrap group gap-[24px_8px] sm:gap-6 hide-scroll ${NEWS_EVENTS_SCROLL_GESTURE_CLASS}`}
          separatorClassName="hidden"
          wrapperClassName="relative group w-full sm:w-full"
        >
          {events.map((e, key) => (
            <div
              className={`flex w-[410px] h-fit md:w-[456px] cursor-pointer items-start ${
                communityStyles.communityLanding.event?.singleContainer ?? ""
              }`}
              key={key}
              onClick={() => handleEventClick(e)}
              ref={observe(e.id)}
            >
              <div
                className={`h-[106px] sm:h-[110px] w-[192px] sm:w-[196px] mr-2 sm:mr-4 relative ${
                  communityStyles.communityLanding.event?.media ?? ""
                }`}
              >
                {e.mediaUrl ? (
                  <img alt="" className={`h-full w-full object-cover`} src={e.mediaUrl} />
                ) : (
                  <div className="h-full w-full  bg-gray-400" />
                )}
                {!isEventPast(e?.end) && (
                  <div className="bg-black opacity-70 absolute h-full w-full top-0 left-0" />
                )}
              </div>
              <div
                className={`space-y-1 sm:space-y-2 mt-1 w-1/2 text-[13px] font-medium ${
                  !isEventPast(e?.end) ? "opacity-50" : ""
                }`}
              >
                <p className={`${communityStyles.communityLanding.event?.title ?? ""}`}>{e.name}</p>
                <p className={`${communityStyles.communityLanding.event?.subtitle ?? ""} truncate`}>
                  {e.locationDisplayCompact}
                </p>
                <p className={`${communityStyles.communityLanding.event?.subtitle ?? ""}`}>
                  {moment(e.start).format(DATE_FORMAT_DAY_MONTH_TIME)}
                </p>
              </div>
            </div>
          ))}
        </ScrollMenu>
      )}
    </div>
  )
}

const EventsV2: FC<{ events: EventListItemFragment[] | undefined }> = ({ events }) => {
  const router = useRouter()
  const customizationData = useCommunityCustomization()
  const observe = useEventImpressions("community-landing")

  // Cards navigate via router.push, which doesn't prefetch; one prefetch warms the
  // shared /c/[code]/event/[id] bundle for every card. Skipped when only past
  // events are listed — those are rarely opened.
  const firstUpcomingEventId = events?.find((e) => moment().isBefore(e.end))?.id
  const communityUrl = customizationData?.url
  useEffect(() => {
    if (!firstUpcomingEventId || !communityUrl) return
    void router.prefetch(`${communityUrl}/event/${firstUpcomingEventId}`).catch(() => {})
  }, [communityUrl, firstUpcomingEventId, router])

  if (!customizationData) return null
  const { styles: communityStyles, url } = customizationData
  const eventsV2Styles = communityStyles.communityLanding.eventsV2

  if (!eventsV2Styles) return null

  const handleEventClick = (event: EventListItemFragment) => {
    if (event.webLink) {
      void router.push(event.webLink)
      return
    }

    void router.push(`${url}/event/${event.id}`)
  }

  return (
    <div className={`flex flex-col ${eventsV2Styles.sectionContainer ?? ""}`}>
      <span className="transform translate-y-[-130px] sm:translate-y-[-90px]" id="events" />
      <h1
        className={`${communityStyles.communityLanding.titles} ${
          eventsV2Styles.sectionTitle ?? ""
        }`}
      >
        Events
      </h1>
      {!events && (
        <div className={`flex overflow-hidden ${eventsV2Styles.scrollMenu?.container ?? ""}`}>
          {[0, 1, 2].map((key) => (
            <div className={eventsV2Styles.item.container} key={key}>
              <div className={eventsV2Styles.item.mediaContainer}>
                <div className={`animate-pulse ${eventsV2Styles.item.placeholderMedia}`} />
              </div>
              <div className={eventsV2Styles.item.content}>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="mt-2 h-3 w-1/2" />
                <Skeleton className="mt-1 h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}
      {events && events.length === 0 && (
        <p className={`sm:ml-0 ml-4 ${communityStyles.communityLanding.emptyOrLoadingState}`}>
          No events posted yet
        </p>
      )}
      {!!events?.length && (
        <ScrollMenu
          itemClassName={eventsV2Styles.scrollMenu?.item}
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
          scrollContainerClassName={`${eventsV2Styles.scrollMenu?.container ?? ""} ${NEWS_EVENTS_SCROLL_GESTURE_CLASS}`}
          separatorClassName="hidden"
          wrapperClassName={eventsV2Styles.scrollMenu?.wrapper}
        >
          {events.map((e, key) => (
            <div
              className={`cursor-pointer ${eventsV2Styles.item.container}`}
              key={key}
              onClick={() => handleEventClick(e)}
              ref={observe(e.id)}
            >
              <div className={eventsV2Styles.item.mediaContainer}>
                {e.mediaUrl ? (
                  <img alt="" className={eventsV2Styles.item.media} src={e.mediaUrl} />
                ) : (
                  <div className={eventsV2Styles.item.placeholderMedia} />
                )}
                {!isEventPast(e?.end) && <div className="bg-black opacity-70 absolute inset-0" />}
              </div>
              <div className={eventsV2Styles.item.content}>
                <p className={`${eventsV2Styles.item.title} line-clamp-2`}>{e.name}</p>
                <p className={eventsV2Styles.item.date}>{formatEvent(e)}</p>
                <p className={eventsV2Styles.item.location}>{e.locationDisplayCompact}</p>
              </div>
            </div>
          ))}
        </ScrollMenu>
      )}
    </div>
  )
}

const Shop: FC<{ openModal: () => void }> = ({ openModal }) => {
  const customizationData = useCommunityCustomization()
  const router = useRouter()
  const track = useTrackUserActivity()
  if (!customizationData) return null
  const { styles, url, code } = customizationData
  const shopPicture = styles.communityLanding.shop.customShopImage
    ? styles.communityLanding.shop.customShopImage
    : "/assets/images/static-shop.png"

  return (
    <div className={`flex flex-col ${styles.communityLanding.shop?.container ?? ""}`}>
      <h1
        className={`${styles.communityLanding.titles} pl-4 sm:pl-8 ${
          styles.communityLanding.shop?.sectionTitle ?? ""
        }`}
      >
        Shop
      </h1>
      <div className="flex flex-col sm:flex-row items-start sm:gap-x-6 px-4 sm:px-8">
        <img
          alt=""
          className={`w-full sm:w-[456px] sm:max-w-none h-[220px] object-cover sm:h-[288px] ${
            styles.communityLanding.shop?.customShopImageStyle ?? ""
          } ${styles.communityLanding.shop?.customShopImage ? "" : "hidden"}`}
          src={shopPicture}
        />
        <div className="flex flex-col items-start gap-y-5 sm:gap-y-6 pt-4 sm:pt-0 sm:w-auto w-full">
          <h2
            className={`${styles.communityLanding.shop?.title ?? ""} ${
              !styles.communityLanding.shop?.customShopImage ? "hidden" : ""
            }`}
          >
            {customizationData.name} Shop
          </h2>
          <p
            className={`${styles.communityLanding.shop.text} max-w-[327px] sm:max-w-[395px] w-full`}
          >
            In our shop you&apos;ll be able to find tickets to our events, including deals and other
            cute things to enjoy as a special member of our community.
          </p>

          <CommunityButton
            button={styles.global.customPrimaryButton}
            onClick={() => {
              if (styles.shopV2) {
                void router.push(`${url}/shop`)
              } else {
                openModal()
                track({
                  data: {
                    communityCode: customizationData?.code,
                    from: "shop",
                  },
                  type: UserActivityEventType.OnlyMobilePopupOpened,
                })
              }
            }}
            primaryButtonStyles={styles.global.primaryButton}
            title="Shop products"
          />
        </div>
      </div>
    </div>
  )
}

interface CommunityProps {
  communityMeta?: {
    thumbnail?: string
    favicon: string
    name: string
    ogTitle?: string
    ogDescription?: string
    ogUrl: string
  }
}

const Community: NextPageWithLayout<CommunityProps> = ({ communityMeta }) => {
  const { user, communities } = useContext(UserContext)
  const track = useTrackUserActivity()
  const router = useRouter()
  const routeCode = router.query.code as string

  const [modalOpen, setModalOpen] = useQueryState<boolean>("useApp", {
    history: "push",
    ...parseAsBoolean.withDefault(false),
  })

  const [modalData, setModalData] = useState<{ title: string; text: string }>()
  const customizationData = useCommunityCustomization()

  const { data } = useCommunityContentQuery(
    graphQLClient,
    { communityId: getCommunityIdBasedOnCode(communities, customizationData?.code ?? "") },
    {
      enabled:
        !!user &&
        !!customizationData &&
        !!getCommunityIdBasedOnCode(communities, customizationData?.code ?? ""),
    },
  )

  useEffect(() => {
    track({
      data: {
        communityCode: customizationData?.code,
      },
      type: UserActivityEventType.InviteLandingOpened,
    })
  }, [customizationData?.code, track])

  if (!customizationData) {
    return null
  }

  const { thumbnail, favicon, name, code, styles, description } = customizationData

  const metaThumbnail = communityMeta?.thumbnail || thumbnail
  const metaFavicon = communityMeta?.favicon || favicon
  const metaName = communityMeta?.name || name
  const metaTitle = communityMeta?.ogTitle || generateCommunityMetaTitle(metaName)
  const metaDescription = communityMeta?.ogDescription || description || ""
  const metaUrl = communityMeta?.ogUrl || toAbsoluteUrl("https://werz.at", `/c/${routeCode}`)

  return (
    <>
      <Head>
        <meta
          content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
          key="viewport"
          name="viewport"
        />
        <meta content="website" property="og:type" />
        <meta content={metaUrl} property="og:url" />
        <meta content={metaThumbnail} property="og:image" />
        <meta content={metaThumbnail} property="og:image:secure_url" />
        <meta content="1200" property="og:image:width" />
        <meta content="630" property="og:image:height" />
        <meta content={metaThumbnail} name="twitter:image" />
        {metaDescription && <meta content={metaDescription} property="og:description" />}
        {metaDescription && <meta content={metaDescription} property="twitter:description" />}
        <meta content="summary_large_image" name="twitter:card" />
        <meta content={metaTitle} property="og:title" />
        <title>{`WERZ x ${metaName}`}</title>
        <link href={metaFavicon} rel="icon" />
      </Head>
      <main className="flex flex-col h-full w-full bg-center bg-cover bg-no-repeat min-h-screen relative">
        <AppNavigation>
          <CommunityContentContainer>
            <div
              className={`mb-16 pt-5 pb-8 space-y-[57px] sm:space-y-20 relative z-1 ${
                styles.communityLanding.container ?? ""
              }`}
            >
              {shouldHide(code) ? <div className="sm:mt-[-68px] mt-[-48px]" /> : null}
              {!shouldHide(code) && styles.communityLanding.announcementsV2 ? (
                <AnnouncementsV2
                  announcements={data?.announcements?.items}
                  openModal={() => {
                    setModalData({
                      text: "News are available only in the WERZ app",
                      title: "News",
                    })
                    void setModalOpen(true)
                  }}
                />
              ) : (
                <Announcements
                  announcements={data?.announcements?.items}
                  openModal={() => {
                    setModalData({
                      text: "News available only in the WERZ app",
                      title: "News",
                    })
                    void setModalOpen(true)
                  }}
                />
              )}
              {!shouldHide(code) && styles.communityLanding.eventsV2 ? (
                <EventsV2 events={data?.eventsV2?.items} />
              ) : (
                <Events events={data?.eventsV2?.items} />
              )}
              <Shop
                openModal={() => {
                  setModalData({
                    text: "The Shop is available only in the WERZ app",
                    title: "Shop products",
                  })
                  void setModalOpen(true)
                }}
              />
            </div>
            <DownloadWERZModal
              isOpen={modalOpen}
              onClose={() => {
                void setModalOpen(null)
                setTimeout(() => setModalData(undefined), 500)
              }}
              text={modalData?.text}
              title={modalData?.title}
            />
          </CommunityContentContainer>
        </AppNavigation>
      </main>
    </>
  )
}

export const DownloadWERZModal: FC<{
  title?: string
  text?: string
  isOpen: boolean
  onClose: () => void
}> = ({ title, text, isOpen, onClose }) => {
  return (
    <ModalBase
      addBgOpacity
      dialogPanelStyles="px-6 sm:px-8 py-16 w-full relative"
      onClose={onClose}
      show={isOpen}
    >
      <img
        alt=""
        className="w-3 h-3 object-contain top-5 right-5 absolute sm:hidden"
        onClick={onClose}
        src="/assets/images/icons/close-modal.svg"
      />
      <div className="text-[#232734] flex flex-col items-center text-[13px] font-mono text-center">
        <h1 className="text-2xl leading-6 uppercase font-bold mb-7">{title}</h1>
        <p className="mb-8 sm:mb-10 leading-6">{text}</p>
        <QRCode
          bgColor="transparent"
          className="hidden sm:block"
          fgColor="#11272D"
          size={105}
          value="https://onelink.to/gzrtq6"
        />
        <a
          className="flex sm:hidden"
          href="https://onelink.to/gzrtq6"
          rel="noopener"
          target="_blank"
        >
          <UbiqButton size={UbiqButtonSize.MEDIUM} title="OPEN WERZ APP" />
        </a>
        <p className="mt-10 mb-4 hidden sm:block">Scan the QR code to download the app.</p>
        <a
          className="hidden sm:block uppercase"
          href="https://onelink.to/gzrtq6"
          rel="noopener"
          target="_blank"
        >
          Download the WERZ app.
        </a>
        <p className="mt-6 leading-6 block sm:hidden">
          Tap the button to be redirected to the app or app store.
        </p>
      </div>
    </ModalBase>
  )
}

export const getServerSideProps: GetServerSideProps<CommunityProps> = async (context) => {
  const { code } = context.params as { code: string }
  const ogUrl = toAbsoluteUrl(getRequestBaseUrl(context.req), `/c/${code}`)

  const communityData = COMMUNITIES[code as keyof typeof COMMUNITIES]

  if (!communityData) {
    return {
      redirect: {
        destination: "https://onelink.to/gzrtq6",
        permanent: false,
      },
    }
  }

  // In-app navigations don't need meta tags, so skip the manifest fetch and let
  // the transition complete immediately.
  if (isClientSideNavigation(context.req)) {
    return {
      props: {
        communityMeta: {
          favicon: communityData.favicon,
          name: communityData.name,
          thumbnail: communityData.customThumbnail || communityData.thumbnail,
          ogUrl,
        },
      },
    }
  }

  // Try S3 route manifest first for meta tags
  // Manifests are keyed by landingCode (or code), which matches the route param after migration
  const manifest = await fetchOgManifest("c", code)

  if (manifest) {
    return {
      props: {
        communityMeta: {
          favicon: communityData.favicon,
          name: communityData.name,
          thumbnail: manifest.image || manifest.fallbackImage,
          ogTitle: manifest.title,
          ogDescription: manifest.description,
          ogUrl,
        },
      },
    }
  }

  // Fallback to static config
  return {
    props: {
      communityMeta: {
        favicon: communityData.favicon,
        name: communityData.name,
        thumbnail: communityData.customThumbnail || communityData.thumbnail,
        ogUrl,
      },
    },
  }
}

export default Community

Community.displayName = "Community"
Community.getLayout = (page: ReactElement) => <AppLayout>{page}</AppLayout>
